import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import stringify from 'json-stable-stringify';
import React from 'react';

import * as ArchivePurposesUtil from 'archive/ArchivePurposesUtil';
import isArchived from 'archive/isArchived';
import {StandardFeature} from 'auth/capabilities/CapabilityConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import * as benchlingUtil from 'BenchlingUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import getConfig from 'getConfig';
import BenchLink from 'link-preview/BenchLink';
import {ExportChipState} from 'notebook/entries/ExportChipState';
import {InternalLink as InternalLinkType} from 'notebook/mediocre/MediocreConstants';
import SearchableText from 'notebook/mediocre/search/SearchableText';
import {
  forwardSearchResultProps,
  getSearchResultPropsForIndex,
  MediocreSearchResultProps,
} from 'notebook/mediocre/search/SearchResultProps';
import {isFailed} from 'schemas/SchemaValidationChecks';
import {getMetadataText} from 'search/saved-searches/SavedSearchUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import * as ChipUtil from 'ui-library/chips/ChipUtil';
import {BLUE_LIGHT} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import APIUtil, {apiIdToIconName} from 'util/APIUtil';
import isValidLink from 'util/isValidLink';
import {MakeAllFieldsOptional} from 'util/Types';

// Line height of text + 4px top/bottom padding
// Keep in sync with font size, and padding of .internalLink-content in `less/components/internal-link.less`
export const INTERNAL_LINK_CHIP_HEIGHT = 24;

// Keep in sync with InternalLinkablMixin subclasses
export const INTERNAL_LINKABLE_MODEL_TYPES = [
  ModelTypeConstants.DATASET,
  ModelTypeConstants.FILE,
  ModelTypeConstants.ANALYTICS_DASHBOARD,
  ModelTypeConstants.CONNECTION,
  ModelTypeConstants.BATCH,
  ModelTypeConstants.STORABLE,
  ModelTypeConstants.ENTITY,
  ModelTypeConstants.TEAM,
  ModelTypeConstants.PROCESS_OUTPUT,
  ModelTypeConstants.PROCESS_TASK,
  ModelTypeConstants.REQUEST,
  ModelTypeConstants.SAVED_SEARCH,
  ModelTypeConstants.SCRATCH_PLATE,
  ModelTypeConstants.STAGE_ENTRY,
  ModelTypeConstants.WORKFLOW,
  ModelTypeConstants.WORKLIST,
  ModelTypeConstants.ANALYSIS,
  ModelTypeConstants.JELLYFISH,
  ModelTypeConstants.MOLECULE,
  ModelTypeConstants.BULK_ASSEMBLY,
  ModelTypeConstants.PROTEIN,
  ModelTypeConstants.SEQUENCE,
  ModelTypeConstants.ENTRY,
  ModelTypeConstants.PROTOCOL,
  ModelTypeConstants.WORKSHEET,
  ModelTypeConstants.PROCESS,
  ModelTypeConstants.MIXTURE,
  ModelTypeConstants.BASIC_FOLDER_ITEM,
  ModelTypeConstants.STUDY,
  ModelTypeConstants.REQUEST_V2_SUBMISSION,
  ModelTypeConstants.REACTION,
  ModelTypeConstants.CANVAS_FOLDER_ARTIFACT,
];

export function isModelInternalLinkable(modelId: string): boolean {
  const modelType = APIUtil.apiIdToMaybeType(modelId);
  if (
    modelType === ModelTypeConstants.CANVAS_FOLDER_ARTIFACT &&
    !(BenchlingConfig.ENABLE_CANVAS_SAVE_TO_FOLDER && BenchlingConfig.ENABLE_CANVAS_FOLDER_ARTIFACT_MENTIONS)
  ) {
    return false;
  }
  return (
    modelType != null &&
    (INTERNAL_LINKABLE_MODEL_TYPES as Readonly<Array<string>>).includes(modelType as string)
  );
}

function isReadOnly(internalLink: InternalLinkType): boolean {
  if (!internalLink.id) {
    return false;
  }
  const isRegistered = Boolean(internalLink.primaryRegistryIdentifier);
  return !isFeatureEnabled({
    feature: StandardFeature.OPEN,
    item: {id: internalLink.id},
    itemState: {isRegistered, isBiologicsOwned: internalLink.isBiologicsOwned === true},
  });
}

function Link(props: InternalLinkProps): JSX.Element {
  const {fileId, displayLabel, id, iconId, name, url, unreadable, savedSearchData, cachedDisplayInfo} =
    props.internalLink;

  if (savedSearchData) {
    return (
      <a className="internalLink-link" href={url}>
        <BenchlingIcon className={css(styles.icon)} icon="search" />
        <span className="internalLink-name">
          <div className={css(styles.name)}>{savedSearchData.name}</div>
        </span>
      </a>
    );
  }

  const icon = iconId || (id ? apiIdToIconName(id) : null);
  const iconNode = icon && <BenchlingIcon className="internalLink-icon" icon={icon} />;
  let effectiveName;
  if (getConfig('ENABLE_FROZEN_CHIPS')) {
    effectiveName = cachedDisplayInfo?.displayLabel || props.chipOverride?.text || displayLabel || name;
  } else {
    effectiveName = displayLabel || name;
  }
  const text = ArchivePurposesUtil.getArchiveItemName({
    ...props.internalLink,
    id: props.internalLink.id ?? '',
    name: effectiveName,
  });

  // searchResultProps are optional since all usages of InternalLink don't require search support
  const searchResultProps = {
    searchResultsTree: props.searchResultsTree ?? null,
    selectedSearchResultTree: props.selectedSearchResultTree ?? null,
  };
  const nameNode = (
    <span className="internalLink-name">
      <SearchableText
        text={text}
        {...(id
          ? getSearchResultPropsForIndex(searchResultProps, id)
          : forwardSearchResultProps(searchResultProps))}
      />
      <SecondaryText internalLink={props.internalLink} />
    </span>
  );

  if (props.linkEnabled && url) {
    const hrefURL = isValidLink(url) ? url : benchlingUtil.pathToURL(url);
    return (
      <BenchLink className="internalLink-link" fileId={fileId} href={hrefURL}>
        {iconNode}
        {nameNode}
      </BenchLink>
    );
  } else if (props.linkEnabled && unreadable) {
    return (
      <a className="internalLink-link">
        {iconNode}
        {nameNode}
      </a>
    );
  } else {
    return (
      <span className="internalLink-link">
        {iconNode}
        {nameNode}
      </span>
    );
  }
}

export function InternalLink(props: InternalLinkProps): JSX.Element {
  const {id, schemaValidationState, savedSearchData} = props.internalLink;

  let tooltipTitle;
  let unreadable;
  if (savedSearchData) {
    tooltipTitle = getMetadataText(
      savedSearchData.description,
      savedSearchData.ownerName,
      savedSearchData.modifiedAt,
      false,
      true
    );
    unreadable = false;
  } else {
    tooltipTitle = props.linkEnabled
      ? ChipUtil.getTooltipTitleFromInternalLink(props.internalLink)
      : undefined;
    unreadable = props.internalLink.cachedDisplayInfo?.unreadable ?? props.internalLink.unreadable;
  }

  return (
    <span
      className={classNames('internalLink', props.className, {
        'internalLink--negative': unreadable || isFailed(schemaValidationState),
        'internalLink--faded': isArchived({...props.internalLink, id: props.internalLink.id ?? ''}),
        'internalLink--isLink': props.linkEnabled,
      })}
      contentEditable={false}
      data-invalid-id={props.internalLink.unreadable ? id : null}
      data-internal-link={stringify(props.internalLink)}
      data-toggle={tooltipTitle ? 'tooltip' : null}
      {...getTestProps({component: 'InternalLink', element: 'root'})}
      title={tooltipTitle}
    >
      {/*
        Add a zero-width space before the icon so that the caret correctly appears
        when placed to the left of a chip located at the beginning of the line.
      */}
      <span>{'\u200B'}</span>
      <span className="internalLink-content">
        <Link {...props} />
      </span>
      {props.onRemove && (
        <BenchlingIcon className="chip-button" icon="cross-thick" onClick={props.onRemove} />
      )}
    </span>
  );
}

interface InternalLinkProps extends MakeAllFieldsOptional<MediocreSearchResultProps> {
  className?: string;
  internalLink: InternalLinkType;
  linkEnabled?: boolean;
  /** If provided, adds an X to the Link to remove it */
  onRemove?: () => void;
  chipOverride?: ExportChipState;
}

// id and url should be present unless the referenced file has been deleted

function SecondaryText({internalLink}: {internalLink: InternalLinkType}): JSX.Element | null {
  const id = internalLink.id || '';
  if (APIUtil.isEntry(id) && BenchlingConfig.ELN_ENABLE_IDS_IN_ENTRY_CHIPS) {
    return (
      <>
        <wbr />
        <span className="internalLink-name-secondary"> {internalLink.humanId}</span>
      </>
    );
  } else if (
    APIUtil.isBiologicalEntity(id) &&
    internalLink.includeRegistryIdInChips &&
    internalLink.primaryRegistryIdentifier
  ) {
    return (
      <>
        <wbr />
        <span className="internalLink-name-secondary"> {internalLink.primaryRegistryIdentifier}</span>
      </>
    );
  }
  return null;
}

export default function ConnectedInternalLink(props: Omit<InternalLinkProps, 'linkEnabled'>): JSX.Element {
  return <InternalLink {...props} linkEnabled={!isReadOnly(props.internalLink)} />;
}

const styles = StyleSheet.create({
  icon: {
    color: BLUE_LIGHT,
  },
  name: {
    color: BLUE_LIGHT,
  },
  toggle: {
    cursor: 'pointer',
  },
});
